import * as React from 'react'
import { FC, useCallback } from 'react'
import SettingsIcon from '@mui/icons-material/Settings'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { AddButton } from './Buttons/AddButton'
import { AvatarButton } from './Buttons/AvatarButton'
import { AppSwitcher } from './Buttons/AppSwitcher'
import { styles } from './useStyles'
import { Logo } from './Logo'
import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { useGetSettingsActive } from '../Settings/hooks'
import Toolbar, { toolbarClasses } from '@mui/material/Toolbar'
import Button from '@mui/material/Button'
import { usePendoInit } from './hooks/usePendoInit'
import { usePublishToolbarHeight } from './hooks/usePublishToolbarHeight'
import { SearchButton } from './Buttons/SearchButton'
import {
  TidalThemeSwitch,
  useTidalThemeActive,
  useTidalThemeEnabled,
} from '@user/features/TidalThemeToggle'
import { TidalThemeSwitchDev } from '@user/features/TidalThemeToggle/TidalThemeSwitchDev'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import AddIcon from '@mui/icons-material/Add'
import { setLeadFilters, setOppsFilters } from '@user/store/guestUser'
import {
  defaultGuestUserLeadFilters,
  defaultGuestUserOpportunityFilters,
} from '@user/store/guestUser/filters'
import { useAppDispatch } from '@user/store/hooks'
import { toolbarHeight } from '@sales-center/common'
import Grid from '@mui/material/Grid'

export const TopToolbar: FC = () => {
  const tidalThemeActive = useTidalThemeActive()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const currentUser = useSelector(selectCurrentUser)

  const { isAdmin } = currentUser
  const isGuestUser = currentUser?.roleName === guestRoleName

  const { isSettingsMode, isSettingsHomePage, currentLocation } =
    useGetSettingsActive()
  const isTidalThemeEnabled = useTidalThemeEnabled()

  const onClickSettings = useCallback(() => {
    if (isSettingsMode) {
      const prevLocation = localStorage.getItem('settings_back_location')
      if (isSettingsHomePage) {
        navigate(String(prevLocation || -1))
      } else {
        navigate('/settings/home')
      }
    } else {
      localStorage.setItem('settings_back_location', currentLocation)
      navigate('/settings/home')
    }
  }, [isSettingsMode, isSettingsHomePage, navigate, currentLocation])

  const handleLinkClick = useCallback(
    (url: string) => {
      if (location.pathname != url || location.search !== '') navigate(url)
    },
    [location.pathname, location.search, navigate],
  )

  const onAddNewLead = useCallback(() => {
    dispatch(setLeadFilters(defaultGuestUserLeadFilters))
    dispatch(setOppsFilters(defaultGuestUserOpportunityFilters))
    navigate('/leads/addLead')
  }, [dispatch, navigate])

  // Pendo init
  usePendoInit()

  const toolbarRef = usePublishToolbarHeight()

  return (
    <Toolbar
      ref={toolbarRef}
      sx={{
        width: '100%',
        minWidth: 0,
        flexDirection: { xs: 'column', sm: 'row' }, // ✅ 2 rows on mobile
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        [`&.${toolbarClasses.root}`]: {
          ...styles.toolbar,
          height: { xs: 'auto', sm: toolbarHeight },
          ...(isSettingsMode && styles.toolbarSettings),
        },
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          sm={currentUser.impersonated ? 4 : 6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: { xs: 'space-between', sm: 'flex-start' },
          }}
        >
          <Logo
            colorful={!isSettingsMode}
            isNavLogo
            isSettingsMode={isSettingsMode}
            style={{ width: '167px', height: '43px' }}
            isGuestUser={isGuestUser}
          />
          {isTidalThemeEnabled && (
            <TidalThemeSwitchDev contrastText={isSettingsMode} />
          )}
        </Grid>
        {currentUser.impersonated && (
          <Grid
            item
            xs={12}
            sm={4}
            p={{ xs: 1, sm: 0 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={styles.impersonate}>
              Impersonating {currentUser.emailAddress}
            </Box>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sm={currentUser.impersonated ? 4 : 6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            mt: { xs: currentUser.impersonated ? 0 : 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 0.6,
            }}
          >
            {!isSettingsMode && !isGuestUser && <SearchButton />}

            {isSettingsMode ? (
              <>
                {isTidalThemeEnabled && <TidalThemeSwitch contrastText />}

                <Button
                  disableRipple
                  color="secondary"
                  variant="contained"
                  onClick={onClickSettings}
                  size="large"
                  sx={{
                    textTransform: 'capitalize',
                    marginLeft: tidalThemeActive ? undefined : '6px',
                    height: tidalThemeActive ? undefined : '47px',
                    minWidth: tidalThemeActive ? undefined : '47px',
                    padding:
                      tidalThemeActive || !isSettingsHomePage
                        ? undefined
                        : '10px',
                  }}
                >
                  {!isSettingsHomePage ? 'Settings Home' : 'Close Settings'}
                </Button>
              </>
            ) : (
              <>
                {isTidalThemeEnabled && <TidalThemeSwitch />}

                {!isGuestUser && (
                  <AddButton handleLinkClick={handleLinkClick} />
                )}

                {isGuestUser && (
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onAddNewLead}
                    data-test-id="guestUserAddNewLeadBtn"
                  >
                    Add Lead
                  </Button>
                )}

                <AvatarButton user={currentUser} />

                {isAdmin && (
                  <Button
                    disableRipple
                    color="primary"
                    variant="outlined"
                    onClick={onClickSettings}
                    data-test-id="settingsIconBtn"
                    sx={{
                      height: '47px',
                      minWidth: '47px',
                      padding: (theme: Theme) => theme.spacing(1),
                    }}
                  >
                    <SettingsIcon color="inherit" />
                  </Button>
                )}

                <AppSwitcher user={currentUser} />
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Toolbar>
  )
}

TopToolbar.displayName = 'TopToolbar'
